import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Rating,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  CheckCircleOutline as PublishIcon,
  HighlightOff as RejectIcon,
  VisibilityOff as UnpublishIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import reviewService from '../../../services/reviewService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import { useDataTable } from '../../../hooks/useDataTable';

const STATUS_COLORS = { PENDING: 'warning', PUBLISHED: 'success', REJECTED: 'default' };

const ReviewsList = () => {
  const { t } = useTranslation();
  const [reviewToDelete, setReviewToDelete] = useState(null);

  // Page, size, search, sort and the status filter live in the URL (/reviews?status=PENDING&page=0&...).
  const table = useDataTable(reviewService.getReviewsPage, {}, {
    onError: () => toast.error(t('reviews.fetchError')),
  });

  const runAction = async (action, successKey, review) => {
    try {
      await action(review.id);
      toast.success(t(successKey));
      table.refresh();
    } catch (error) {
      toast.error(error.response?.data?.message || t('reviews.actionError'));
    }
  };

  const handleDeleteConfirm = async () => {
    const review = reviewToDelete;
    setReviewToDelete(null);
    await runAction(reviewService.deleteReview, 'reviews.deleteSuccess', review);
  };

  const columns = [
    { field: 'createdAt', headerName: t('common.date'), width: 120, valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString('fr-FR') : '') },
    { field: 'productName', headerName: t('reviews.product'), width: 220 },
    {
      field: 'authorName',
      headerName: t('reviews.author'),
      width: 170,
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {params.value}
          {params.data.verifiedPurchase && <Chip label={t('reviews.verified')} size="small" color="info" variant="outlined" />}
        </Box>
      ),
    },
    {
      field: 'rating',
      headerName: t('reviews.rating'),
      width: 140,
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Rating value={params.value} readOnly size="small" />
        </Box>
      ),
    },
    {
      field: 'comment',
      headerName: t('reviews.comment'),
      flex: 1,
      minWidth: 280,
      sortable: false,
      tooltipValueGetter: (params) => params.data?.comment,
      cellRenderer: (params) => (
        <span title={params.data.comment}>
          {params.data.title ? <strong>{params.data.title} — </strong> : null}
          {params.data.comment}
        </span>
      ),
    },
    {
      field: 'status',
      headerName: t('common.status'),
      width: 130,
      cellRenderer: (params) => (
        <Chip label={t(`reviews.status.${params.value}`)} color={STATUS_COLORS[params.value] || 'default'} size="small" />
      ),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 150,
      sortable: false,
      cellRenderer: (params) => {
        const review = params.data;
        return (
          <GridActionsCell
            actions={[
              {
                key: 'publish',
                icon: PublishIcon,
                title: t('reviews.publish'),
                color: 'success',
                hidden: review.status === 'PUBLISHED',
                onClick: () => runAction(reviewService.publishReview, 'reviews.publishSuccess', review),
              },
              {
                key: 'reject',
                icon: RejectIcon,
                title: t('reviews.reject'),
                color: 'warning',
                hidden: review.status !== 'PENDING',
                onClick: () => runAction(reviewService.rejectReview, 'reviews.rejectSuccess', review),
              },
              {
                key: 'unpublish',
                icon: UnpublishIcon,
                title: t('reviews.unpublish'),
                color: 'default',
                hidden: review.status !== 'PUBLISHED',
                onClick: () => runAction(reviewService.unpublishReview, 'reviews.unpublishSuccess', review),
              },
              {
                key: 'delete',
                icon: DeleteIcon,
                title: t('common.delete'),
                color: 'error',
                onClick: () => setReviewToDelete(review),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>{t('reviews.title')}</Typography>

      <Card>
        <CardContent>
          <TableSearch
            placeholder={t('reviews.searchPlaceholder')}
            value={table.searchInput}
            onChange={table.handleSearchChange}
            sx={{ mb: 2 }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={table.params.status || 'all'}
            onChange={(_, next) => next && table.updateParams({ status: next })}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="all">{t('common.filterAll')}</ToggleButton>
            <ToggleButton value="PENDING">{t('reviews.status.PENDING')}</ToggleButton>
            <ToggleButton value="PUBLISHED">{t('reviews.status.PUBLISHED')}</ToggleButton>
            <ToggleButton value="REJECTED">{t('reviews.status.REJECTED')}</ToggleButton>
          </ToggleButtonGroup>
          <AppDataGrid
            rows={table.data.content}
            columns={columns}
            loading={table.loading}
            paginationMode="server"
            rowCount={table.data.totalElements}
            paginationModel={table.paginationModel}
            onPaginationModelChange={table.handlePaginationModelChange}
            sortModel={table.sortModel}
            onSortModelChange={table.handleSortChange}
            pageSizeOptions={[10, 20, 50, 100]}
            getRowId={(row) => row.id}
          />
        </CardContent>
      </Card>

      <Dialog open={Boolean(reviewToDelete)} onClose={() => setReviewToDelete(null)}>
        <DialogTitle>{t('reviews.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('reviews.deleteConfirm', { name: reviewToDelete?.authorName })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewToDelete(null)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewsList;
